import React, { useState } from "react";
import PropTypes from "prop-types";
import { useCompetition, useStartCompetition, useOverride, usePromote } from "./useForecastLab";
import { toLeaderboard, promoteDiff } from "./leaderboard";
import CompetitionToolbar from "./CompetitionToolbar";
import CandidateChips from "./CandidateChips";
import LeaderboardTable from "./LeaderboardTable";

const DEFAULT_CANDIDATES = ["prophet", "autoarima", "ses", "tidemodel"];

// Short one-line purpose (kept visible) + the full how-to (moved into a title
// tooltip) so the header stays compact but still says where the selection is used.
const PURPOSE = {
  insights: "winners power the Forecast & Predictability dashboard",
  allocation: "winners power the live cost-allocation forecasts",
};
const HELP = {
  insights:
    "Each series is forecast by the model that wins a 30-day holdout backtest. Run a competition to refresh the rankings, then promote the winners (plus any overrides) to make them live on the Forecast & Predictability dashboard. Prophet is the fallback for unmapped series.",
  allocation:
    "Each source is forecast by the model that wins a 30-day holdout backtest. Run a competition to refresh the rankings, then apply the winners (plus any overrides) to make them live on the allocation forecasts. Prophet is the fallback for unmapped sources.",
};

// ModelStudio drives the per-series (insights) or per-source (allocation) model
// competition. The `scope` prop is threaded into every read/mutation so the
// backend operates on the right leaderboard, and into the child views so they
// relabel ("Source" vs "Series", "Apply now" vs "Promote") and, for allocation,
// render a per-source band drill-down.
export default function ModelStudio({ scope }) {
  const { data, isLoading } = useCompetition(scope);
  const start = useStartCompetition();
  const override = useOverride();
  const promote = usePromote();
  const [candidates, setCandidates] = useState(DEFAULT_CANDIDATES);
  const [confirming, setConfirming] = useState(false);
  const rows = toLeaderboard(data?.results, data?.config);

  const isAllocation = scope === "allocation";
  const noun = isAllocation ? "source" : "series";
  const diff = promoteDiff(rows);

  return (
    <div className="space-y-3">
      {/* Cohesive header: title + purpose + grouped actions, then the candidate
          selector. The leaderboard renders as its own card below (matching the
          Financial Control table). */}
      <div>
        <CompetitionToolbar
          meta={data}
          running={start.isLoading}
          onRun={() => start.mutate({ scope, candidates, holdout_days: 30 })}
          scope={scope}
          subtitle={PURPOSE[scope] || PURPOSE.insights}
          help={HELP[scope] || HELP.insights}
          overrides={rows.filter((r) => r.source === "override").length}
          promoting={promote.isLoading}
          onPromote={() => setConfirming(true)}
        />
        <CandidateChips candidates={candidates} onChange={setCandidates} />
      </div>

      {/* Promote is a high-stakes action — it changes the numbers executives see.
          Confirm with a diff before pushing the selection live. */}
      {confirming && (
        <div className="rounded-card border border-warning bg-warning-soft px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="max-w-2xl text-sm text-text-2">
              <span className="font-semibold">
                {isAllocation ? "Apply to live allocation forecasts?" : "Promote to the dashboard?"}
              </span>{" "}
              {diff.changing === 0
                ? "No model changes — this re-applies the current selection."
                : `${diff.changing} ${noun}${diff.changing === 1 ? "" : "s"} will change model`}
              {diff.changing > 0 && diff.poor > 0 && (
                <span className="font-medium text-danger">
                  {" "}
                  — {diff.poor} with poor or unreliable accuracy
                </span>
              )}
              {diff.changing > 0 ? ". " : " "}
              Prophet remains the fallback for unmapped {noun}s.
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="inline-flex items-center rounded-ctrl border border-card-bd bg-card px-3 py-1.5 text-sm font-medium text-text-2 hover:bg-surface-2"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  promote.mutate({ scope });
                  setConfirming(false);
                }}
                disabled={promote.isLoading}
                className="inline-flex items-center rounded-ctrl bg-success px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isAllocation ? "Confirm apply" : "Confirm promote"}
              </button>
            </div>
          </div>
        </div>
      )}
      <LeaderboardTable
        rows={rows}
        loading={isLoading}
        scope={scope}
        nameMap={data?.source_names || {}}
        onOverride={(series, algorithm) =>
          override.mutate({ scope, series_name: series, algorithm })
        }
      />
    </div>
  );
}

ModelStudio.propTypes = {
  /** "insights" (default) or "allocation". */
  scope: PropTypes.string,
};

ModelStudio.defaultProps = {
  scope: "insights",
};
